import moment from "moment";
import { useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  useEvChargerDashboardQuery,
  useViewEvChargerSessionActiveQuery,
} from "../../../redux/api/ev-charger/evChargerAPI";
import EvChargerGauge from "../../../shared/components/ev-charger-gauge/ev-charger-gauge";
import Reveal from "../../../shared/components/reveal/reveal";
import { AppRoute } from "../../../shared/oversight-core/interfaces/app-routes";
import MaterialIcon from "../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import SpinnerModal from "../../../shared/oversight-core/ui-elements/spinner/spinner";
import Helper from "../../../shared/oversight-core/utils/helpers";
import ChangeTimeSettingsModal from "./components/change-time-settings-modal/change-time-settings-modal";
import styles from "./home.module.scss";

const Home = () => {
  const navigate = useNavigate();

  const [showTimeSettingsModal, setShowTimeSettingsModal] = useState(false);

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const { data: dashboardData, isFetching: isDashboardFetching } =
    useEvChargerDashboardQuery({
      year: currentYear,
      month: currentMonth,
    });
  const { data: activeSessionData, isFetching: isSessionFetching } =
    useViewEvChargerSessionActiveQuery();

  const isActiveSession = activeSessionData?.hasActiveSession;
  const currentEnergy = Number(
    Helper.roundTo2(Number(activeSessionData?.energyDeliveredKwh ?? 0)),
  );
  const chargingPercentage = activeSessionData?.socPercent ?? 0;
  const currentAmps = activeSessionData?.currentAmps ?? 0;
  const temperatureCelsius = activeSessionData?.temperatureCelsius ?? 0;
  const voltage = activeSessionData?.voltage ?? 0;

  const allCount = dashboardData?.totalChargers ?? 0;
  const availableCount = dashboardData?.availableChargers ?? 0;
  const totalUsage = dashboardData?.totalEnergyDeliveredKwh
    ? Number(Helper.roundTo2(Number(dashboardData?.totalEnergyDeliveredKwh)))
    : 0;
  const totalChargers = dashboardData?.totalSessions ?? 0;

  const currentMonthYear = moment().format("MMMM YYYY");

  return (
    <div className={styles.homeContainer}>
      {isActiveSession ? (
        <>
          <Reveal>
          <EvChargerGauge
            ringColor="#7483CC"
            isWavy={!!currentEnergy}
            innerColor="bg-background"
          >
            {currentEnergy ? (
              <div className="d-flex flex-column align-items-center mb-4">
                <MaterialIcon icon="flash_on" color="#FFC72F" size={22} fill />
                <span className="font-size-14 font-weight-500 text-light mt-1">
                  Charging&nbsp;
                  {chargingPercentage ? `${chargingPercentage}%` : ``}
                </span>
              </div>
            ) : (
              <></>
            )}
            <div className="d-flex flex-column align-items-center text-center">
              <div className="font-weight-500 font-size-32 text-dark m-0 lh-1">
                {Helper.formatPower(Number(currentEnergy ?? 0)).value}
                &nbsp;
                {Helper.formatPower(Number(currentEnergy ?? 0)).unit}
              </div>
            </div>
          </EvChargerGauge>
          </Reveal>

          <Reveal delay={90}>
          <button
            type="button"
            className={`${styles.timerButton} d-flex align-items-center gap-2 mt-4 mb-4`}
            onClick={() => setShowTimeSettingsModal(true)}
          >
            <span>Charging Timer</span>
            <MaterialIcon icon="chevron_right" color="var(--light)" size={20} />
          </button>
          </Reveal>

          <Row
            className="g-3 justify-content-center w-100 ui-stagger"
            style={{ maxWidth: "540px" }}
          >
            <Col xs={12} sm={6}>
              <div className={styles.statCard}>
                <div className={`${styles.iconBadge} ${styles.bgGreen}`}>P</div>
                <div className="d-flex align-items-center gap-2">
                  <span className={styles.valueText}>
                    {currentAmps || "-"} A
                  </span>
                </div>
              </div>
            </Col>

            <Col xs={12} sm={6}>
              <div className={styles.statCard}>
                <div className={`${styles.iconBadge} ${styles.bgBlue}`}>
                  <MaterialIcon icon="charger" color="#ffffff" size={20} />
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className={styles.valueText}>{currentEnergy} kWh</span>
                </div>
              </div>
            </Col>

            <Col xs={12} sm={6}>
              <div className={styles.statCard}>
                <div className={`${styles.iconBadge} ${styles.bgLime}`}>
                  <MaterialIcon icon="thermometer" color="#ffffff" size={20} />
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className={styles.valueText}>
                    {temperatureCelsius || "-"} C°
                  </span>
                </div>
              </div>
            </Col>

            <Col xs={12} sm={6}>
              <div className={styles.statCard}>
                <div className={`${styles.iconBadge} ${styles.bgCyan}`}>V</div>
                <div className="d-flex align-items-center gap-2">
                  <span className={styles.valueText}>{voltage || "-"}V</span>
                </div>
              </div>
            </Col>
          </Row>
        </>
      ) : (
        <>
          <Reveal>
          <EvChargerGauge
            ringColor="#7483CC"
            isWavy={false}
            innerColor="bg-background"
          >
            <div className="d-flex flex-column align-items-center justify-content-center gap-4 h-100">
              <MaterialIcon icon="flash_on" color="#FFC72F" size={24} fill />
              <div className="d-flex align-items-center gap-2">
                <span className="font-weight-500 font-size-36 text-dark m-0 lh-1">
                  {availableCount}/{allCount}
                </span>
                <span className="font-size-13 text-light font-weight-400 lh-sm text-start">
                  Chargers
                  <br />
                  Available
                </span>
              </div>
              <MaterialIcon icon="location_on" color="#7483CC" size={22} fill />
            </div>
          </EvChargerGauge>
          </Reveal>

          <Reveal delay={90}>
          <div className="d-flex align-items-center gap-2 mt-4 mb-4">
            <MaterialIcon icon="wifi" color="var(--text-dark)" size={20} />
            <span className="text-dark font-size-16 font-weight-400">
              Use RFID to Connect
            </span>
          </div>
          </Reveal>

          <Row
            className="g-3 justify-content-center w-100 ui-stagger"
            style={{ maxWidth: "540px" }}
          >
            <Col xs={12} sm={6}>
              <button
                type="button"
                className={`${styles.rfidCard} h-100 w-100 text-start`}
                onClick={() => navigate(AppRoute.HISTORY)}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <span className="text-link font-size-14 font-weight-500">
                    {currentMonthYear}
                  </span>
                  <MaterialIcon
                    icon="chevron_right"
                    color="var(--text-dark)"
                    size={20}
                  />
                </div>
                <div className="mt-2">
                  <div className="font-size-13 text-dark font-weight-400">
                    Total Usage
                  </div>
                  <div className="font-weight-600 font-size-24 text-dark m-0">
                    {totalUsage} kWh
                  </div>
                </div>
              </button>
            </Col>

            <Col xs={12} sm={6}>
              <div
                className={`${styles.rfidCard} ${styles.rfidCardStatic} h-100 w-100 text-start`}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <span className="text-link font-size-14 font-weight-500">
                    {currentMonthYear}
                  </span>
                </div>
                <div className="mt-2">
                  <div className="font-size-13 text-dark font-weight-400">
                    Total Charge Sessions
                  </div>
                  <div className="font-weight-600 font-size-32 text-dark m-0">
                    {totalChargers}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </>
      )}
      <ChangeTimeSettingsModal
        show={showTimeSettingsModal}
        evChargerId={activeSessionData?.evChargerId}
        onClose={() => setShowTimeSettingsModal(false)}
        onCancel={() => setShowTimeSettingsModal(false)}
      />
      <SpinnerModal show={isDashboardFetching || isSessionFetching} />
    </div>
  );
};

export default Home;
